// src/pages/job_openings/create/CandidateConfirmation/CandidateConfirmation.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import LogoWhite from '../../../../assets/images/logos/white-bg-logo-hiacuity.png';
import { Button } from '../../../../ui';
import {
  ITimeSlot,
  confirmTimeSlot,
  getInterviewRoundCandidateDetails,
  getInterviewRoundSlots,
} from '../../../../utils/api_service/SmartInterview';
import { getImageSrc } from '../../../../utils/helpers/image-utils';
import { RootState } from '../../../../utils/redux_store/store';

// parse path or query
function useInterviewParams() {
  const { interviewId: pId, token: pToken } =
    useParams<{ interviewId?: string; token?: string }>();
  const qs = new URLSearchParams(useLocation().search);
  const rawToken = pToken || qs.get('token') || '';
  const interviewId = pId || qs.get('interviewId') || '';
  const token = rawToken ? decodeURIComponent(rawToken) : '';
  return { interviewId, token };
}

export const CandidateConfirmation: React.FC = () => {
  const { interviewId, token } = useInterviewParams();

  // candidate/me
  const [candidateName, setCandidateName] = useState('');
  const [interviewType, setInterviewType] = useState('');
  const [slots, setSlots] = useState<ITimeSlot[]>([]);

  // picker state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<ITimeSlot | null>(null);
  const [errors, setErrors] = useState<{ selectedSlot?: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState('');
  const [candidateTimeZone, setCandidateTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  // post-confirmation state
  const [confirmed, setConfirmed] = useState(false);
  const [declined, setDeclined] = useState(false);

  // store token for confirmTimeSlot if needed
  useEffect(() => {
    if (token) {
      localStorage.setItem('authToken', token);
      sessionStorage.setItem('authToken', token);
    }
  }, [token]);

  // fetch candidate info
  useEffect(() => {
    if (!interviewId || !token) return;
    getInterviewRoundCandidateDetails(interviewId, token)
      .then((r) => {
        if (!r.ok) throw new Error(`Status ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const u = data.user;
        setCandidateName(`${u.firstName} ${u.lastName}`);
        setCandidateEmail(u.email);
        setCandidateTimeZone(u.timeZone || candidateTimeZone);
        setInterviewType(data.interviewType);
      })
      .catch((e) => {
        alert('Error loading candidate information');
      });
  }, [interviewId, token]);

  // fetch slots
  useEffect(() => {
    if (!interviewId || !token) return;
    setLoading(true);
    getInterviewRoundSlots(interviewId, token)
      .then((r) => {
        if (!r.ok) throw new Error(`Status ${r.status}`);
        return r.json();
      })
      .then((data) => setSlots(data.timeSlots || []))
      .catch(() => alert('Error loading available times'))
      .finally(() => setLoading(false));
  }, [interviewId, token]);

  // group slots by day
  const grouped = useMemo(() => {
    const map: Record<string, ITimeSlot[]> = {};
    slots.forEach((s) => {
      const day = new Date(s.startTime * 1000).toISOString().split('T')[0];
      map[day] = map[day] ? [...map[day], s] : [s];
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => a.startTime - b.startTime),
    );
    return map;
  }, [slots]);

  // helpers to format
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  const fmtTime = (ts: number) =>
    new Date(ts * 1000).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  // confirm handler
  const handleConfirm = () => {
    if (!selectedSlot) {
      setErrors({ selectedSlot: true });
      return;
    }
    setLoading(true);
    confirmTimeSlot(
      {
        interviewStartTime: selectedSlot.startTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      interviewId,
      token,
    )
      .then((r) => {
        if (!r.ok) return r.json().then((e) => Promise.reject(e));
        setConfirmed(true);
      })
      .catch(() => alert('❌ Failed to confirm interview'))
      .finally(() => setLoading(false));
  };

  // decline handler
  const handleDecline = () => {
    setDeclined(true);
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);
  const companyName = useSelector((state: RootState) => state.logo.companyName);

  // invalid link
  if (!token || !interviewId) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
        <div className='bg-white p-6 rounded-lg shadow-lg text-center max-w-sm'>
          <h2 className='text-xl font-semibold text-red-600 mb-4'>
            Invalid Link
          </h2>
          <p className='text-gray-700'>
            This link seems invalid or expired. Please check your email or
            contact support.
          </p>
        </div>
      </div>
    );
  }

  // initial loader
  if (loading && !slots.length && !confirmed && !declined) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4'></div>
          <p className='text-gray-600'>Loading interview details…</p>
        </div>
      </div>
    );
  }

  // --- DECLINED UI ---
  if (declined) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
        <div className='bg-white rounded-lg shadow-lg p-6 text-center justify-center items-center flex flex-col max-w-md'>
          <h2 className='text-2xl font-semibold text-gray-800 mb-4'>
            You’ve declined this interview
          </h2>
          <p className='text-gray-600 mb-6'>
            If you change your mind, this link will expire in a few hours.
          </p>
          <Button
              label='Refresh'
              variant='primary'
              onClick={() => window.location.reload()}
              width='120px'
          />
        </div>
      </div>
    );
  }

  // --- CONFIRMED UI ---
  if (confirmed && selectedSlot) {
    // find the date key
    const dayKey = new Date(selectedSlot.startTime * 1000)
      .toISOString()
      .split('T')[0];
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
        <div className='bg-white rounded-lg shadow-lg p-6 max-w-md text-center'>
          <h2 className='text-2xl font-semibold text-green-600 mb-4'>
            Thank you, { candidateName }!
          </h2>
          <p className='text-gray-700 mb-2'>Your interview is scheduled for:</p>
          <p className='text-lg font-medium'>
            { fmtDate(dayKey) } at { fmtTime(selectedSlot.startTime) }
          </p>
        </div>
      </div>
    );
  }

  // --- DEFAULT: show picker ---
  return (
    <div className=' flex justify-center bg-gray-50 h-[100vh] py-8 px-4'>
      <div className='max-w-2xl w-full bg-white h-auto rounded-2xl shadow-lg p-6 space-y-6 min-h-[600px]'>
        { /* Header card */ }
        <div className='flex items-center justify-between'>
          <img
              src={getImageSrc(companyLogo, LogoWhite)}
              className='h-10 object-contain'
              alt='Company Logo'
          />
          <h2 className='text-lg font-semibold'>
            Hi, { candidateName || 'there' }
          </h2>
        </div>

        <div className='bg-primary-background rounded-xl p-4 space-y-3'>
          <div className='flex items-center space-x-4'>
            { /* Avatar fallback */ }
            <div className='w-12 h-12 bg-primary-dark rounded-full flex items-center justify-center text-white font-bold text-xl'>
              { candidateName.charAt(0) || '?' }
            </div>
            <div>
              <h3 className='text-xl font-semibold text-gray-800'>
                { candidateName }
              </h3>
              <p className='text-sm text-gray-600'>{ candidateEmail }</p>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4 text-sm text-gray-700'>
            <div>
              <span className='font-medium'>Interview Type:</span>{ ' ' }
              { interviewType }
            </div>
            <div>
              <span className='font-medium'>Time Zone:</span>{ ' ' }
              { candidateTimeZone }
            </div>
          </div>
        </div>

        <p className='text-gray-700'>
          You’re invited to a <strong>{ interviewType }</strong>. Please select
          your preferred time:
        </p>

        { /* Date picker */ }
        <div className=' flex flex-col items-start border-t pt-5 gap-10 md:flex-row'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Select Date
            </label>
            <div className='grid grid-cols-1 gap-2 max-h-60 overflow-y-auto'>
              { Object.keys(grouped)
                .sort()
                .map((d) => (
                  <button
                      key={d}
                      onClick={() => {
                        setSelectedDate(d);
                        setSelectedSlot(null);
                        setErrors({});
                      }}
                      className={`p-3 text-left rounded-lg border transition-colors ${
                        selectedDate === d
                          ? 'border-prbg-primary-dark bg-primary-dark/10 text-primary-main'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <div className='font-medium'>{ fmtDate(d) }</div>
                    <div className='text-sm text-gray-500'>
                      {
                        // Show only the count of available (not booked) slots
                        grouped[d].filter((s) => !s.isBooked).length
                      }{ ' ' }
                      slot
                      { grouped[d].filter((s) => !s.isBooked).length !== 1
                        ? 's'
                        : '' }{ ' ' }
                      available
                    </div>
                  </button>
                )) }
            </div>
            { !Object.keys(grouped).length && (
              <div className='text-center py-8 text-gray-500'>
                No available dates found
              </div>
            ) }
          </div>

          { /* TIME SLOTS PICKER */ }
          { selectedDate && (
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Select Time
              </label>
              <div className='grid grid-cols-3 gap-2 max-h-60 overflow-y-auto'>
                { grouped[selectedDate]
                  .filter((s) => !s.isBooked) // <--- HIDE BOOKED SLOTS
                  .map((s) => {
                    const isSelected = selectedSlot?.startTime === s.startTime;
                    function classNames(
                      ...classes: (string | false | undefined | null)[]
                    ): string {
                      return classes.filter(Boolean).join(' ');
                    }
                    return (
                      <button
                          key={s.startTime}
                          type='button'
                          onClick={() => {
                            setSelectedSlot(s);
                            setErrors({});
                          }}
                          className={classNames(
                            'p-3 text-center rounded-lg border transition-colors',
                            isSelected
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                          )}
                      >
                        <div className='font-medium'>
                          { fmtTime(s.startTime) }
                        </div>
                        { s.endTime && (
                          <div className='text-xs text-gray-500'>
                            to { fmtTime(s.endTime) }
                          </div>
                        ) }
                      </button>
                    );
                  }) }
              </div>
              { /* Show message if no unbooked slots */ }
              { grouped[selectedDate].filter((s) => !s.isBooked).length ===
                0 && (
                <div className='text-center py-4 text-gray-500'>
                  No available time slots for this date
                </div>
              ) }
            </div>
          ) }

          { errors.selectedSlot && (
            <div className='text-red-600 text-sm'>
              Please select a time slot to continue
            </div>
          ) }
        </div>

        { /* Actions */ }
        <div className='flex justify-end space-x-3 pt-4 border-t border-gray-200'>
          <Button label='Decline' variant='secondary' onClick={handleDecline} />
          <Button
              label={loading ? 'Confirming…' : 'Confirm Interview'}
              variant='primary'
              onClick={handleConfirm}
              disabled={!selectedSlot || loading}
          />
        </div>
      </div>
    </div>
  );
};
